/**
 * IA Console shell — Atendente IA & SDR (OpenSpec #2.5).
 *
 * Rota: `/configuracao/ia/atendente-sdr`. Header com título + badge versão
 * (vinda do bloco builder via state) + 4 sub-tabs:
 *   - Atendente IA (functional, ver AtendenteIaBuilder)
 *   - SDR · Campos (placeholder OpenSpec #5)
 *   - Playground (chat de teste contra prompt do Atendente IA + variáveis)
 *   - Histórico (timeline + diff + restore das versões do Atendente IA)
 *
 * Permissões:
 *   - Qualquer role autenticado da org vê.
 *   - Edição só para owner | admin | gerente | super_admin — passada como prop
 *     canEdit para o builder.
 */
import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { AtendenteIaBuilder } from './AtendenteIaBuilder';
import { UsoTab } from './UsoTab';
import HistoricoTab from './HistoricoTab';
import SdrCamposTab from './SdrCamposTab';
import PlaygroundTab from './PlaygroundTab';

type SubTab = 'builder' | 'sdr-campos' | 'playground' | 'uso' | 'historico';

// (`PlaceholderTab` removido — todas as 5 sub-tabs já têm implementação real.)

const SUB_TABS: { id: SubTab; label: string }[] = [
  { id: 'builder', label: 'Atendente IA' },
  { id: 'sdr-campos', label: 'SDR · Campos' },
  { id: 'playground', label: 'Playground' },
  { id: 'uso', label: 'Uso' },
  { id: 'historico', label: 'Histórico' },
];

export const AtendenteSdrConsole: React.FC = () => {
  const { usuario, isOwner, isAdmin, isGerente, isSuperAdmin } = useAuth();
  const [active, setActive] = useState<SubTab>('builder');

  const role = usuario?.role || '';
  const canEdit = isOwner || isAdmin || isGerente || isSuperAdmin;
  void role; // role available for future per-block permission checks

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc' }}>
      <div style={{ padding: '14px 28px 0' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 14,
            paddingBottom: 12,
          }}
        >
          <div>
            <div
              style={{
                fontSize: 11,
                color: '#64748b',
                textTransform: 'uppercase',
                letterSpacing: 0.6,
                fontWeight: 700,
              }}
            >
              Configuração · IA
            </div>
            <h1 style={{ margin: '2px 0 0', fontSize: 22, fontWeight: 700 }}>
              Atendente IA &amp; SDR
            </h1>
          </div>
        </div>

        <div
          style={{
            display: 'flex',
            gap: 4,
            borderBottom: '1px solid #e2e8f0',
            marginBottom: 0,
          }}
        >
          {SUB_TABS.map((t) => {
            const isActive = active === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setActive(t.id)}
                style={{
                  padding: '10px 16px',
                  border: 0,
                  borderBottom: isActive ? '2px solid #6366f1' : '2px solid transparent',
                  background: 'transparent',
                  color: isActive ? '#3730a3' : '#64748b',
                  fontWeight: isActive ? 700 : 500,
                  fontSize: 14,
                  cursor: 'pointer',
                  marginBottom: -1,
                }}
              >
                {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {active === 'builder' && <AtendenteIaBuilder canEdit={canEdit} />}
      {active === 'sdr-campos' && <SdrCamposTab canEdit={canEdit} />}
      {active === 'playground' && <PlaygroundTab canRun={canEdit} />}
      {active === 'uso' && <UsoTab />}
      {active === 'historico' && (
        <HistoricoTab
          canRestore={canEdit}
          onRestoreApplied={() => {
            // Quando restore termina, força o usuário a voltar pro Atendente IA
            // pra ver os blocos recém-restaurados. Volta automaticamente:
            setActive('builder');
          }}
        />
      )}
    </div>
  );
};
